import { MondayItemType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import React, { useContext, useState, useCallback } from "react";

export type BreadcrumbItem = {
  id: string;
  name: string;
  level: number;
  item: MondayItemType;
};

type MondayImportItemsContextProps = {
  breadcrumbs: BreadcrumbItem[];
  currentPath: string;
  currentItem: MondayItemType | null;
  addBreadcrumb: (item: MondayItemType) => void;
  navigateToBreadcrumb: (index: number) => void;
  clearBreadcrumbs: () => void;
  goBack: () => void;
};

type MondayImportItemsProviderProps = {
  children: React.ReactNode;
};

const MondayImportItemsContext = React.createContext<MondayImportItemsContextProps>({
  breadcrumbs: [],
  currentPath: "",
  currentItem: null,
  addBreadcrumb: () => {},
  navigateToBreadcrumb: () => {},
  clearBreadcrumbs: () => {},
  goBack: () => {},
});

export const useMondayImportItemsContext = () => useContext(MondayImportItemsContext);

export const MondayImportItemsContextProvider = ({ children }: MondayImportItemsProviderProps) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  // Generate path from breadcrumbs
  const currentPath = breadcrumbs.map((item) => item.name).join(" > ");

  // Get the current item being viewed (last item in breadcrumbs)
  const currentItem = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].item : null;

  // Add a new breadcrumb to the navigation
  const addBreadcrumb = useCallback((item: MondayItemType) => {
    setBreadcrumbs((previous) => {
      // If we're adding an item that's already in the breadcrumbs, navigate to it instead
      const existingIndex = previous.findIndex((b) => b.id === item.id);
      if (existingIndex !== -1) {
        return previous.slice(0, existingIndex + 1);
      }

      // Add the new breadcrumb with level information
      const newBreadcrumb: BreadcrumbItem = {
        id: item.id,
        name: item.name,
        level: previous.length,
        item: item,
      };
      return [...previous, newBreadcrumb];
    });
  }, []);

  // Navigate to a specific breadcrumb by index
  const navigateToBreadcrumb = useCallback((index: number) => {
    setBreadcrumbs((previous) => previous.slice(0, index + 1));
  }, []);

  // Clear all breadcrumbs
  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  // Go back one level
  const goBack = useCallback(() => {
    setBreadcrumbs((previous) => previous.slice(0, -1));
  }, []);

  const value: MondayImportItemsContextProps = {
    breadcrumbs,
    currentPath,
    currentItem,
    addBreadcrumb,
    navigateToBreadcrumb,
    clearBreadcrumbs,
    goBack,
  };

  return <MondayImportItemsContext.Provider value={value}>{children}</MondayImportItemsContext.Provider>;
};
